import {
  FileText,
  Undo2,
  Truck,
  FileSignature,
  Tag,
  Package,
  ShoppingCart,
  MessageSquare,
} from "lucide-react";
import { FeatureCard } from "../components/ui/FeatureCard";
import { PageHeader } from "../components/ui/PageHeader";
import { PortalState } from "../components/ui/PortalState";
import { usePortalAuth } from "../hooks/usePortalAuth";
import { usePortalResource } from "../hooks/usePortalResource";
import { apiGet } from "../api/portal-client";

interface Counts {
  invoices: number;
  creditNotes: number;
  deliveryNotes: number;
  contracts: number;
  orders: number;
  prices: number;
}

/**
 * Tableau de bord — lanceur des espaces du client.
 *
 * W7-B — un compteur INCONNU ne se lit plus « rien à voir ici ». L'échec de
 * `/me/counts` se résumait à une ligne rouge sous le titre pendant que les huit
 * cartes restaient muettes : un client dont la requête tombait voyait
 * exactement le même écran qu'un client sans aucun document. L'état est
 * désormais annoncé (hors-ligne AVANT erreur, accès refusé distinct) et un
 * compteur indisponible affiche « — ». Un vrai zéro reste un zéro.
 *
 * Les huit espaces restent CLIQUABLES dans tous les cas : le client doit pouvoir
 * ouvrir « Mes factures » même quand le compteur est mort.
 */
export function DashboardPage() {
  const { client } = usePortalAuth();
  const { data, status, error, reload } = usePortalResource<Counts>(() =>
    apiGet<Counts>("/me/counts"),
  );

  /**
   * `undefined` = compteur encore en lecture (la carte n'affiche aucun nombre),
   * `null` = compteur indisponible (« — »), nombre = valeur du serveur, zéro
   * compris. Un champ absent d'une réponse n'est PAS un zéro.
   */
  const countOf = (key: keyof Counts): number | null | undefined => {
    if (status === "loading") return undefined;
    if (status !== "ready") return null;
    return typeof data?.[key] === "number" ? data[key] : null;
  };

  return (
    <main className="max-w-5xl mx-auto px-4 py-6">
      <PageHeader
        title={`Bonjour ${client?.name ?? ""}`}
        subtitle="Voici vos commandes, vos documents et votre grille tarifaire."
      />

      <div className="mb-6">
        <PortalState status={status} label="vos compteurs" error={error} onRetry={reload} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <FeatureCard
          to="/orders"
          icon={ShoppingCart}
          label="Mes commandes"
          count={countOf("orders")}
          testId="count-orders"
          accent="terracotta"
        />
        <FeatureCard
          to="/invoices"
          icon={FileText}
          label="Mes factures"
          count={countOf("invoices")}
          testId="count-invoices"
          accent="terracotta"
        />
        <FeatureCard
          to="/credit-notes"
          icon={Undo2}
          label="Mes avoirs"
          count={countOf("creditNotes")}
          testId="count-credit-notes"
          accent="slate"
        />
        <FeatureCard
          to="/delivery-notes"
          icon={Truck}
          label="Mes bons de livraison"
          count={countOf("deliveryNotes")}
          testId="count-delivery-notes"
          accent="terracotta"
        />
        <FeatureCard
          to="/contracts"
          icon={FileSignature}
          label="Mes contrats"
          count={countOf("contracts")}
          testId="count-contracts"
          accent="slate"
        />
        <FeatureCard
          to="/prices"
          icon={Tag}
          label="Mes tarifs"
          count={countOf("prices")}
          testId="count-prices"
          accent="terracotta"
        />
        {/* Ces deux espaces n'ont pas de compteur côté API : pas de nombre, et
            surtout pas un « — » qui laisserait croire à une panne. */}
        <FeatureCard to="/product-sheets" icon={Package} label="Fiches produits" accent="slate" />
        <FeatureCard to="/messages" icon={MessageSquare} label="Mes messages" accent="terracotta" />
      </div>
    </main>
  );
}
